/**
 * The agent container's SIP/MIP queue: what runs now, what is next, what needs a person.
 *
 * Wears the snapshot editor's own look (see pe3d/pe3dPanelKit.js): flat rows on the
 * panel surface with hairlines between them, states as coloured text rather than
 * chips, recessed controls. A 30-row task must stay readable, so a task is one line
 * until it is opened, and it opens by itself only when it has something to show.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Switch, TextField, Tooltip } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import FlagIcon from "@material-ui/icons/Flag";
import ReplayIcon from "@material-ui/icons/Replay";
import TuneIcon from "@material-ui/icons/Tune";
import { useDrag, useDrop } from "react-dnd";
import AdsManagerClaims from "./containerAdsManagerClaims";
import AgentMarkdown from "./agentMarkdown";
import { BAR_BG, CONTROL_BG, DARK_SCROLLBAR_CSS, FIELD_LABEL, PANEL_BG, PROPS_PANEL_CSS, SECTION_DIVIDER } from "./panelPalette";
import {
  DIALOG_PAPER_PROPS, DIALOG_TEXT, ERROR_TEXT, FieldRow, MUTED, OK_TEXT, OUTLINED_BUTTON_STYLE, PanelHint, PanelMessage, ROW_HOVER, SectionHeader, TEXT, WARNING_TEXT,
} from "../pe3d/pe3dPanelKit";
import { jobQueue } from "../../modules/snapshotEditorApi";

const POLL_MS = 5000;
const DRAG_TYPE = "jobQueueTask";

// [label, tone]. A tone is one meaning everywhere: active, good, needs a person, broken, idle.
export const TASK_STATE = {
  intake: ["Reading", "active"], decision: ["Needs decision", "warn"], producing: ["In production", "active"], checking: ["Folder check", "active"],
  fixing: ["Fixing", "active"], done: ["Done", "ok"], attention: ["Needs attention", "warn"], stopped: ["Stopped", "idle"],
};
export const ROW_STATE = {
  pending: ["Waiting", "idle"], preparing: ["Preparing", "active"], running: ["Running", "active"], merging: ["Merging", "active"],
  merge_resolving: ["Resolving conflict", "active"], merge_stuck: ["Merge stuck", "bad"], done: ["Done", "ok"], failed: ["Failed", "bad"], stopped: ["Stopped", "idle"],
};
const ROLE = { lead: "Lead", fix: "Fix" };
const AUTOMATIONS = { "asana-webhook": "Audience+", "asana-adsmanager": "Ads Manager" };
const SOURCES = { ...AUTOMATIONS, custom: "Custom" };
const RETRYABLE = new Set(["failed", "stopped", "merge_stuck"]);
const BROKEN = new Set(["failed", "merge_stuck"]);
const ACTIVE = new Set(["preparing", "running", "merging", "merge_resolving"]);

const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 };

const useStyles = makeStyles({
  // The app root centres its text, which every panel has to undo.
  root: { height: "100%", overflowY: "auto", overflowX: "hidden", textAlign: "left", backgroundColor: PANEL_BG, color: TEXT },
  bar: {
    position: "sticky", top: 0, zIndex: 2, display: "flex", alignItems: "center", gap: 8, minHeight: 40, padding: "6px 8px 6px 12px",
    backgroundColor: BAR_BG, borderBottom: `1px solid ${SECTION_DIVIDER}`,
  },
  barTitle: { fontSize: 13, fontWeight: 600 },
  barMeta: { flex: 1, fontSize: 11, color: FIELD_LABEL, ...ellipsis },
  banner: { fontSize: 12, lineHeight: 1.5, padding: "6px 12px", color: WARNING_TEXT, borderBottom: `1px solid ${SECTION_DIVIDER}` },
  problem: { fontSize: 12, lineHeight: 1.5, padding: "6px 12px", color: ERROR_TEXT, borderBottom: `1px solid ${SECTION_DIVIDER}` },
  section: { padding: "0 6px" },

  slot: { display: "grid", gridTemplateColumns: "44px minmax(0, 1fr) auto", columnGap: 8, alignItems: "center", minHeight: 24, padding: "0 6px", fontSize: 12 },
  slotId: { color: MUTED, fontSize: 11 },
  slotJob: { color: TEXT, ...ellipsis },
  slotFree: { color: "#6f7278" },
  slotOther: { fontSize: 11, color: MUTED, padding: "6px 6px 2px" },

  task: { borderBottom: `1px solid ${SECTION_DIVIDER}`, borderTop: "2px solid transparent" },
  taskHead: {
    display: "grid", gridTemplateColumns: "16px 16px minmax(0, 1fr) auto", columnGap: 4, alignItems: "center", padding: "6px 6px 6px 2px",
    borderRadius: 4, cursor: "pointer", userSelect: "none", "&:hover": { backgroundColor: ROW_HOVER },
  },
  handle: { display: "flex", color: "#6f7278", cursor: "grab", "&:hover": { color: FIELD_LABEL } },
  caret: { fontSize: 18, color: MUTED, display: "block", marginLeft: -2 },
  taskName: { fontSize: 13, color: TEXT, ...ellipsis },
  taskMeta: { fontSize: 11, color: MUTED, marginTop: 1, ...ellipsis },
  track: { height: 2, marginTop: 4, borderRadius: 1, backgroundColor: SECTION_DIVIDER, overflow: "hidden" },
  fill: { height: "100%", borderRadius: 1, transition: "width 0.3s" },
  status: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, whiteSpace: "nowrap" },
  body: { padding: "0 6px 8px 34px" },

  row: { padding: "1px 0", borderRadius: 4 },
  rowLine: { display: "grid", gridTemplateColumns: "18px minmax(0, 1fr) auto auto 24px", columnGap: 6, alignItems: "center", minHeight: 26, padding: "0 2px 0 4px", borderRadius: 4, "&:hover": { backgroundColor: ROW_HOVER } },
  rowOpenable: { cursor: "pointer" },
  rowSeq: { fontSize: 11, color: MUTED, textAlign: "right", fontVariantNumeric: "tabular-nums" },
  rowMain: { display: "flex", alignItems: "baseline", gap: 6, minWidth: 0, fontSize: 12, color: TEXT },
  rowLabel: { ...ellipsis },
  stats: { fontSize: 11, color: MUTED, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  tag: { flex: "0 0 auto", fontSize: 10, fontWeight: 600, letterSpacing: 0.4, color: MUTED, textTransform: "uppercase" },
  tagLead: { color: FIELD_LABEL },
  rowError: { fontSize: 11, lineHeight: 1.5, color: ERROR_TEXT, padding: "0 4px 4px 28px", wordBreak: "break-word" },
  reply: {
    margin: "2px 4px 6px 28px", padding: "6px 10px", maxHeight: 320, overflow: "auto",
    backgroundColor: CONTROL_BG, border: `1px solid ${SECTION_DIVIDER}`, borderRadius: 4,
  },
  question: { fontSize: 12, lineHeight: 1.5, color: TEXT, padding: "2px 0 6px" },
  answerLine: { display: "flex", alignItems: "flex-start", gap: 6 },
  footer: { display: "flex", alignItems: "center", gap: 6, marginTop: 6 },
  quiet: {
    padding: "2px 6px", border: "none", borderRadius: 4, background: "none", color: MUTED, fontSize: 11, fontFamily: "inherit", cursor: "pointer",
    "&:hover": { color: TEXT, backgroundColor: ROW_HOVER },
  },
  quietDanger: { color: ERROR_TEXT, "&:hover": { color: ERROR_TEXT } },
  tail: { height: 16, borderTop: "2px solid transparent" },
  switchRow: { display: "flex", alignItems: "center", gap: 6, minHeight: 28, padding: "0 6px", fontSize: 12, color: FIELD_LABEL },
  saveLine: { padding: "4px 6px 8px" },
});

const smallButton = { ...OUTLINED_BUTTON_STYLE, fontSize: 12, padding: "2px 8px", minWidth: 0 };
const iconStyle = { fontSize: 16 };
const rowAction = { padding: 3, color: FIELD_LABEL };

export function formatDuration(ms) {
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export const formatCost = (usd) => `$${usd.toFixed(2)}`;

/** Rows finished before durations were recorded only have their start and end. */
function rowDuration(row) {
  if (row.durationMs) return row.durationMs;
  const span = Date.parse(row.finishedAt) - Date.parse(row.startedAt);
  return span > 0 ? span : 0;
}

/** "4m 12s · $0.51", once a row has ended. Nothing for a row that never ran. */
function statsLine(durationMs, costUsd) {
  return [durationMs > 0 ? formatDuration(durationMs) : null, costUsd > 0 ? formatCost(costUsd) : null].filter(Boolean).join(" · ");
}

function useTone() {
  const theme = useTheme();
  return (tone) => ({ active: theme.palette.primary.main, ok: OK_TEXT, warn: WARNING_TEXT, bad: ERROR_TEXT }[tone] || MUTED);
}

function Status({ state, table, label }) {
  const classes = useStyles();
  const color = useTone();
  const [text, tone] = table[state] || [state, "idle"];
  return (
    <span className={classes.status} style={{ color: color(tone) }}>
      {tone === "active" && <CircularProgress size={9} thickness={6} style={{ color: "inherit" }} />}
      {label || text}
    </span>
  );
}

function RowItem({ task, row, act }) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const lead = task.rows.find((item) => item.role === "lead" && item.product === row.product);
  const canPromote = row.state === "pending" && row.role === "sibling" && lead && RETRYABLE.has(lead.state);
  const label = row.stem || row.deliveryName || row.request || (row.role === "fix" ? (row.files || []).join(", ") : row.product);
  const openable = Boolean(row.lastReply);
  const stats = row.finishedAt && !ACTIVE.has(row.state) ? statsLine(rowDuration(row), row.costUsd) : "";
  const press = (action) => (event) => {
    event.stopPropagation();
    act(action, { taskId: task.id, rowId: row.id });
  };
  return (
    <div className={classes.row} data-row={row.id}>
      <div className={`${classes.rowLine} ${openable ? classes.rowOpenable : ""}`} role={openable ? "button" : undefined}
        aria-label={openable ? "Last reply" : undefined} aria-expanded={openable ? open : undefined} onClick={openable ? () => setOpen(!open) : undefined}>
        <span className={classes.rowSeq}>{row.seq}</span>
        <span className={classes.rowMain}>
          <span className={classes.tag}>{row.type}</span>
          {ROLE[row.role] && <span className={`${classes.tag} ${classes.tagLead}`}>{ROLE[row.role]}</span>}
          <Tooltip title={label || ""} placement="top" enterDelay={600}><span className={classes.rowLabel}>{label}</span></Tooltip>
        </span>
        <span className={classes.stats}>{stats}</span>
        <span title={row.slotId || undefined}><Status state={row.state} table={ROW_STATE} label={row.limitWaiting ? "Waiting for an account" : row.blocked ? "Waiting for lead" : undefined} /></span>
        <span>
          {RETRYABLE.has(row.state) && (
            <Tooltip title="Retry"><IconButton size="small" aria-label="Retry" style={rowAction} onClick={press("retry")}><ReplayIcon style={iconStyle} /></IconButton></Tooltip>
          )}
          {canPromote && (
            <Tooltip title="Make this row the lead: it does the shared preparation"><IconButton size="small" aria-label="Make lead" style={rowAction} onClick={press("promote")}><FlagIcon style={iconStyle} /></IconButton></Tooltip>
          )}
        </span>
      </div>
      {row.error && <div className={classes.rowError}>{row.error}</div>}
      {openable && open && <div className={classes.reply}><AgentMarkdown text={row.lastReply} /></div>}
    </div>
  );
}

function Question({ task, onAnswer }) {
  const classes = useStyles();
  const [answer, setAnswer] = useState("");
  return (
    <div>
      <div className={classes.question}>{task.question}</div>
      <div className={classes.answerLine}>
        <TextField fullWidth multiline value={answer} placeholder="Your answer" inputProps={{ "aria-label": "Answer" }} onChange={(event) => setAnswer(event.target.value)} />
        <Button size="small" variant="contained" color="primary" disabled={!answer.trim()} style={{ textTransform: "none", flex: "0 0 auto" }}
          onClick={() => onAnswer(task.id, answer).then(() => setAnswer(""))}>Answer</Button>
      </div>
    </div>
  );
}

function StopControl({ task, act }) {
  const classes = useStyles();
  const [confirming, setConfirming] = useState(false);
  if (task.stopMode) return <span className={classes.taskMeta}>Stopping: running jobs are finishing.</span>;
  if (!confirming) return <button type="button" className={classes.quiet} onClick={() => setConfirming(true)}>Stop task</button>;
  return (
    <>
      <button type="button" className={`${classes.quiet} ${classes.quietDanger}`} onClick={() => { setConfirming(false); act("stop", { taskId: task.id }); }}>Running jobs will be killed. Stop?</button>
      <button type="button" className={classes.quiet} onClick={() => setConfirming(false)}>Keep going</button>
    </>
  );
}

function startsOpen(task) {
  return task.state === "decision" || task.rows.some((row) => ACTIVE.has(row.state) || BROKEN.has(row.state));
}

function TaskGroup({ task, position, act, onAnswer, draggable, onMove }) {
  const classes = useStyles();
  const color = useTone();
  const [open, setOpen] = useState(() => startsOpen(task));
  const [, drag, preview] = useDrag(() => ({ item: { type: DRAG_TYPE, taskId: task.id }, canDrag: draggable }), [task.id, draggable]);
  const [{ over }, drop] = useDrop(() => ({
    accept: DRAG_TYPE,
    canDrop: (item) => draggable && item.taskId !== task.id,
    drop: (item) => onMove(item.taskId, task.id),
    collect: (monitor) => ({ over: monitor.isOver() && monitor.canDrop() }),
  }), [task.id, draggable, onMove]);
  const finished = ["done", "attention", "stopped"].includes(task.state);
  const done = task.rows.filter((row) => row.state === "done").length;
  const broken = task.rows.filter((row) => BROKEN.has(row.state)).length;
  // The whole task, its intake agent included, once it is over.
  const totalMs = task.rows.reduce((sum, row) => sum + rowDuration(row), task.intakeStats?.durationMs || 0);
  const totalUsd = task.rows.reduce((sum, row) => sum + (row.costUsd || 0), task.intakeStats?.costUsd || 0);
  const meta = [SOURCES[task.source] || task.source, task.rows.length ? `${done}/${task.rows.length} done` : null, finished ? statsLine(totalMs, totalUsd) : null].filter(Boolean).join(" · ");
  const checkErrors = task.check && !task.check.ok ? task.check.errors.map((error) => error.code || error.message).join(", ") : null;
  return (
    <div ref={(node) => preview(drop(node))} className={classes.task} style={over ? { borderTopColor: color("active") } : undefined} data-task={task.id}>
      <div className={classes.taskHead} role="button" aria-label="Rows" aria-expanded={open} onClick={() => setOpen(!open)}>
        {draggable ? (
          <Tooltip title={`Position ${position}: drag to reorder`} placement="left" enterDelay={600}>
            <span ref={drag} className={classes.handle} aria-label="Drag" onClick={(event) => event.stopPropagation()}><DragIndicatorIcon style={iconStyle} /></span>
          </Tooltip>
        ) : <span />}
        {open ? <ArrowDropDownIcon className={classes.caret} /> : <ArrowRightIcon className={classes.caret} />}
        <div style={{ minWidth: 0 }}>
          <Tooltip title={task.name} placement="top" enterDelay={600}><div className={classes.taskName}>{task.name}</div></Tooltip>
          <div className={classes.taskMeta}>
            {meta}
            {task.pendingEdit && <span style={{ color: WARNING_TEXT }}> · Edit waiting</span>}
            {broken > 0 && <span style={{ color: ERROR_TEXT }}>{` · ${broken} failed`}</span>}
          </div>
          {task.rows.length > 0 && !finished && (
            <div className={classes.track}><div className={classes.fill} style={{ width: `${(done / task.rows.length) * 100}%`, backgroundColor: color("active") }} /></div>
          )}
        </div>
        <Status state={task.state} table={TASK_STATE} />
      </div>
      <Collapse in={open} unmountOnExit>
        <div className={classes.body}>
          {task.state === "decision" && <Question task={task} onAnswer={onAnswer} />}
          {checkErrors && <div className={classes.rowError} style={{ padding: "0 0 4px" }}>{`Folder check: ${checkErrors}`}</div>}
          {task.intakeStats?.costUsd > 0 && (
            <div className={classes.rowLine}>
              <span />
              <span className={classes.rowMain}><span className={classes.tag}>Intake</span></span>
              <span className={classes.stats}>{statsLine(task.intakeStats.durationMs, task.intakeStats.costUsd)}</span>
              <span /><span />
            </div>
          )}
          {task.rows.map((row) => <RowItem key={row.id} task={task} row={row} act={act} />)}
          {!finished && <div className={classes.footer}><StopControl task={task} act={act} /></div>}
        </div>
      </Collapse>
    </div>
  );
}

/** Dropping on a task puts it before that task, so the end of the list needs its own target. */
function ListEnd({ onMove }) {
  const classes = useStyles();
  const color = useTone();
  const [{ over }, drop] = useDrop(() => ({
    accept: DRAG_TYPE,
    drop: (item) => onMove(item.taskId, null),
    collect: (monitor) => ({ over: monitor.isOver() }),
  }), [onMove]);
  return <div ref={drop} className={classes.tail} style={over ? { borderTopColor: color("active") } : undefined} />;
}

/** Which task and row a slot is on. */
export function slotJob(slot, tasks) {
  const task = tasks.find((item) => item.id === slot.job?.taskId);
  const row = task?.rows.find((item) => item.id === slot.job.rowId);
  return task ? { name: task.name, seq: row?.seq } : null;
}

function Slots({ view }) {
  const classes = useStyles();
  return (
    <div className={classes.section}>
      <SectionHeader title="Now running" />
      {view.slots.map((slot) => {
        const job = slotJob(slot, view.tasks);
        return (
          <div key={slot.slotId} className={classes.slot}>
            <span className={classes.slotId}>{slot.slotId.replace("slot-", "Slot ")}</span>
            {job
              ? <span className={classes.slotJob}>{job.name}</span>
              : <span className={classes.slotFree}>Free</span>}
            <span className={classes.slotId}>{[job?.seq ? `row ${job.seq}` : null, slot.retiring ? "closing" : null].filter(Boolean).join(" · ")}</span>
          </div>
        );
      })}
      {!view.slots.length && <PanelMessage style={{ padding: "4px 6px" }}>The queue is off: automations open sessions.</PanelMessage>}
      {view.general && <div className={classes.slotOther}>{`Other work: ${view.general.running} running, ${view.general.queued} waiting (limit ${view.general.limit})`}</div>}
    </div>
  );
}

function NumberRow({ label, value, disabled, onChange, hint }) {
  return (
    <>
      <FieldRow label={label}>
        <TextField fullWidth type="number" value={value} disabled={disabled} inputProps={{ min: 1, max: 16, "aria-label": label }} onChange={(event) => onChange(event.target.value)} />
      </FieldRow>
      <PanelHint>{hint}</PanelHint>
    </>
  );
}

function Limits({ view, act }) {
  const classes = useStyles();
  const { settings } = view;
  const [queueSlots, setQueueSlots] = useState(String(settings.queueSlots));
  const [generalSlots, setGeneralSlots] = useState(String(settings.generalSlots));
  const valid = [queueSlots, generalSlots].every((value) => /^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= 16);
  const dirty = Number(queueSlots) !== settings.queueSlots || Number(generalSlots) !== settings.generalSlots;
  return (
    <>
      <SectionHeader title="Machine" />
      <NumberRow label="Queue slots" value={queueSlots} disabled={!view.canEditLimits} onChange={setQueueSlots} hint="Worktrees kept ready. Lowering never kills a running job." />
      <NumberRow label="Other work" value={generalSlots} disabled={!view.canEditLimits} onChange={setGeneralSlots} hint={`Sessions, Jira, reports. In effect now: ${view.effectiveGeneralSlots}.`} />
      {view.canEditLimits && dirty && (
        <div className={classes.saveLine}>
          <Button size="small" style={smallButton} disabled={!valid} onClick={() => act("settings", { settings: { queueSlots: Number(queueSlots), generalSlots: Number(generalSlots) } })}>Save</Button>
        </div>
      )}
      <SectionHeader title="Through the queue" />
      {Object.entries(AUTOMATIONS).map(([source, label]) => (
        <label key={source} className={classes.switchRow}>
          <Switch size="small" color="primary" checked={settings.automations[source] === true} disabled={!view.canEditLimits}
            inputProps={{ "aria-label": `${label} queue` }} onChange={(event) => act("settings", { settings: { automations: { [source]: event.target.checked } } })} />
          {label}
        </label>
      ))}
    </>
  );
}

function CustomTaskDialog({ act, onClose }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    // On success the dialog is gone, so only a refusal has anything left to update.
    if (await act("add", { name, description })) onClose();
    else setSaving(false);
  };
  return (
    <Dialog open fullWidth maxWidth="xs" onClose={onClose} PaperProps={DIALOG_PAPER_PROPS}>
      <DialogTitle>Custom task</DialogTitle>
      <DialogContent>
        <TextField fullWidth autoFocus label="Task name" value={name} inputProps={{ maxLength: 200, "aria-label": "Task name" }} onChange={(event) => setName(event.target.value)} />
        <TextField fullWidth multiline minRows={4} label="What to build" style={DIALOG_TEXT.field} value={description} inputProps={{ "aria-label": "What to build" }} onChange={(event) => setDescription(event.target.value)} />
        <div style={DIALOG_TEXT.hint}>How many SIPs or MIPs, for which product, with any links. It joins the end of the queue. If the text leaves it unclear, a question appears on the task.</div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="primary" disabled={saving || !name.trim() || !description.trim()} onClick={submit}>Add to queue</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function JobQueuePanel({ baseID, isActive, canEditClaims }) {
  const classes = useStyles();
  const [view, setView] = useState(null);
  const [error, setError] = useState("");
  const [showFinished, setShowFinished] = useState(false);
  const [showLimits, setShowLimits] = useState(false);
  const [adding, setAdding] = useState(false);
  const disposed = useRef(false);

  const accept = useCallback((result) => {
    if (disposed.current) return false;
    if (!result.ok) { setError(result.error?.message || result.error?.error || "Could not load the queue."); return false; }
    setView(result.data);
    setError("");
    return true;
  }, []);

  const act = useCallback((action, payload) => jobQueue(baseID, action, payload).then(accept).catch((err) => setError(err.message)), [baseID, accept]);
  const answer = useCallback((taskId, text) => act("answer", { taskId, answer: text }), [act]);
  const move = useCallback((taskId, beforeTaskId) => act("move", { taskId, beforeTaskId }), [act]);

  useEffect(() => {
    disposed.current = false;
    if (!isActive) return () => { disposed.current = true; };
    const load = () => jobQueue(baseID).then(accept).catch((err) => { if (!disposed.current) setError(err.message); });
    void load();
    const timer = setInterval(load, POLL_MS);
    return () => { disposed.current = true; clearInterval(timer); };
  }, [baseID, isActive, accept]);

  const running = view ? view.slots.filter((slot) => slot.job).length : 0;
  return (
    <div className={`${classes.root} proto-props-panel proto-dark-scroll`}>
      <style>{PROPS_PANEL_CSS}</style>
      <style>{DARK_SCROLLBAR_CSS}</style>

      <div className={classes.bar}>
        <span className={classes.barTitle}>Queue</span>
        <span className={classes.barMeta}>{view ? `${running}/${view.slots.length} slots · ${view.tasks.length} open` : ""}</span>
        <Button size="small" style={smallButton} startIcon={<AddIcon style={{ fontSize: 14 }} />} onClick={() => setAdding(true)}>Custom task</Button>
        <Tooltip title="Limits">
          <IconButton size="small" aria-label="Limits" aria-expanded={showLimits} style={{ padding: 4, color: showLimits ? TEXT : FIELD_LABEL }} onClick={() => setShowLimits(!showLimits)}><TuneIcon style={{ fontSize: 18 }} /></IconButton>
        </Tooltip>
      </div>

      {error && <div className={classes.problem} role="alert">{error}</div>}
      {view?.limitPauseUntil && <div className={classes.banner}>{`Every account is at its usage limit. The queue is waiting and continues by itself around ${new Date(view.limitPauseUntil).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.`}</div>}
      {view?.diskLow && <div className={classes.banner}>{`Less than ${view.settings.minFreeGb} GB free on disk: no new jobs start, running ones continue.`}</div>}

      <Collapse in={showLimits} unmountOnExit>
        <div className={classes.section} style={{ borderBottom: `1px solid ${SECTION_DIVIDER}`, paddingBottom: 6 }}>
          {view && <Limits key={`${view.settings.queueSlots}/${view.settings.generalSlots}`} view={view} act={act} />}
          <AdsManagerClaims baseID={baseID} isActive={isActive && showLimits} canEdit={canEditClaims} />
        </div>
      </Collapse>

      {view && <Slots view={view} />}

      {view && (
        <div className={classes.section}>
          <SectionHeader title={view.tasks.length ? `In line · ${view.tasks.length}` : "In line"} />
          {view.tasks.map((task, index) => <TaskGroup key={task.id} task={task} position={index + 1} act={act} onAnswer={answer} onMove={move} draggable={view.tasks.length > 1} />)}
          {view.tasks.length > 1 && <ListEnd onMove={move} />}
          {!view.tasks.length && <PanelMessage style={{ padding: "4px 6px 8px" }}>No tasks in the queue.</PanelMessage>}
        </div>
      )}

      {view?.finished?.length > 0 && (
        <div className={classes.section}>
          <SectionHeader title={`Finished · ${view.finished.length}`}
            right={<button type="button" className={classes.quiet} onClick={() => setShowFinished(!showFinished)}>{showFinished ? "Hide" : "Show"}</button>} />
          {showFinished && view.finished.map((task, index) => <TaskGroup key={task.id} task={task} position={index + 1} act={act} onAnswer={answer} onMove={move} draggable={false} />)}
        </div>
      )}

      {adding && <CustomTaskDialog act={act} onClose={() => setAdding(false)} />}
    </div>
  );
}
